import { Color, Group, Matrix, Path, Point, Project } from "paper";
import { PaperUtils } from "../utilities/paper-utils";
import { defaultGridLineSettings, defaultGridTextSettings, defaultMeasurementLineSettings, defaultMeasurementTextSettings, defaultEntityPathSettings, pixelAttenuationOffset, defaultMeasurementPanelSettings, MEASUREMENT_PANEL_HEIGHT_RATIO, LEGEND_ITEMS, defaultLegendSettings, } from "../settings/altitude-view-settings";
import { detectCorners, determineSlopeFormat } from "./section-view-utils";
export function filterVisibleLegendItems(data) {
    return LEGEND_ITEMS.filter((item) => {
        if (item.dataKey === "pathPoints") {
            const value = data.pathPoints;
            return value.length > 0 && (value[0]?.length ?? 0) > 0;
        }
        if (item.dataKey === "flatGroundDataOutlines") {
            const value = data.flatGroundDataOutlines;
            if (value == null)
                return false;
            return value.some((outline) => outline.length > 0 && (outline[0]?.length ?? 0) > 0);
        }
        const value = data[item.dataKey];
        return value.some((outline) => outline.length > 0 && (outline[0]?.length ?? 0) > 0);
    });
}
export class SectionView {
    _project;
    _view;
    _canvas;
    _zoomFactor = 1.05;
    _scaleFactor;
    _pathMap;
    // to keep all grid lines together and easy to delete all grid lines at same time.
    _gridGroup;
    _gridSizeInPixel = 50;
    _entityRenderGroup;
    _measurementLinesGroup;
    _ratioSlopeTextRenderGroup;
    _percentSlopeTextRenderGroup;
    _measurementPanelGroup;
    _measurementPanelData;
    _legendGroup;
    _visibleLegendItems = [];
    _labels;
    _gridText;
    isDragging = false;
    startPoint;
    _presentation;
    _currentEntityId;
    get currentEntityID() {
        return this._currentEntityId;
    }
    setLabels(labels) {
        this._labels = labels;
    }
    constructor(presentation) {
        this._presentation = presentation;
        this._canvas = document.createElement("canvas");
        this._pathMap = new Map();
        // Setup canvas here
        this._canvas.style.width = "100%";
        this._canvas.style.height = "100%";
        this._canvas.style.backgroundColor = "#566573";
        this._project = new Project(this._canvas);
        this._project.activate();
        this._view = this._project.view;
        this._view.transform(new Matrix(1, 0, 0, -1, 0, 0));
        //to keep all grid lines in one group
        this._gridGroup = new Group();
        this._gridText = new Group();
        //Create grid lines
        this.changeGridSize(this._gridSizeInPixel);
        this._canvas.addEventListener("wheel", (event) => {
            PaperUtils.zoom(event, this._zoomFactor, this._view);
            this.changeGridSize(this._gridSizeInPixel);
            this._attenuate();
        });
        this._canvas.addEventListener("mousedown", (event) => {
            this.isDragging = true;
            this.startPoint = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
        });
        this._canvas.addEventListener("mousemove", (event) => {
            if (this.isDragging) {
                const point = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
                this._view.translate(point.subtract(this.startPoint));
                this.changeGridSize(this._gridSizeInPixel);
                this._view.update();
            }
        });
        this._canvas.addEventListener("mouseup", () => {
            this.isDragging = false;
        });
        this._ratioSlopeTextRenderGroup = new Group();
        this._percentSlopeTextRenderGroup = new Group();
    }
    _activateView() {
        this._project.activate();
    }
    clearView() {
        if (this._entityRenderGroup) {
            this._entityRenderGroup.removeChildren();
            this._entityRenderGroup = undefined;
        }
        if (this._measurementLinesGroup) {
            this._measurementLinesGroup.removeChildren();
            this._measurementLinesGroup = undefined;
        }
        if (this._ratioSlopeTextRenderGroup) {
            this._ratioSlopeTextRenderGroup.removeChildren();
            this._ratioSlopeTextRenderGroup = undefined;
        }
        if (this._percentSlopeTextRenderGroup) {
            this._percentSlopeTextRenderGroup.removeChildren();
            this._percentSlopeTextRenderGroup = undefined;
        }
        if (this._measurementPanelGroup) {
            this._measurementPanelGroup.removeChildren();
            this._measurementPanelGroup = undefined;
        }
        this._measurementPanelData = undefined;
        if (this._legendGroup) {
            this._legendGroup.removeChildren();
            this._legendGroup = undefined;
        }
        this._visibleLegendItems = [];
        //remove grid text
        this._gridText.removeChildren();
        this._currentEntityId = undefined;
    }
    _attenuate() {
        //attenuate Entities
        this._entityRenderGroup?.children.forEach((l) => PaperUtils.attenuateLines(l, this._view, defaultEntityPathSettings.defaultPixelSize));
        //attenuate lines
        this._measurementLinesGroup?.children.forEach((path) => PaperUtils.attenuateLines(path, this._view, defaultMeasurementLineSettings.defaultPixelSize));
        //attenuate texts
        this._ratioSlopeTextRenderGroup?.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultMeasurementTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.slopeTextOffsetSectionView, this._view);
        });
        this._percentSlopeTextRenderGroup?.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultMeasurementTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.slopeTextOffsetSectionView, this._view);
        });
        // Redraw measurement panel at current zoom level to maintain pixel-constant sizes
        if (this._measurementPanelGroup && this._measurementPanelData) {
            this._measurementPanelGroup.removeChildren();
            this._drawMeasurementPanelContents();
        }
        // Redraw legend at current zoom level to maintain pixel-constant sizes
        if (this._legendGroup) {
            this._legendGroup.removeChildren();
            this._drawLegend();
        }
        //attenuate Grids
        this._gridGroup?.children.forEach((line) => PaperUtils.attenuateLines(line, this._view, defaultGridLineSettings.defaultPixelSize));
        this._gridText?.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultGridTextSettings.defaultPixelSize);
        });
        this._view.requestUpdate();
    }
    resizeView(size) {
        if (size.height && size.width) {
            this._project.view.viewSize.height = size.height;
            this._project.view.viewSize.width = size.width;
            this._view.update();
            this.changeGridSize(this._gridSizeInPixel);
        }
    }
    _drawLegend() {
        if (this._visibleLegendItems.length === 0 || !this._legendGroup)
            return;
        const s = defaultLegendSettings;
        const paddingProj = PaperUtils.pixelToProjectDist(s.paddingPx, this._view);
        const itemSpacingProj = PaperUtils.pixelToProjectDist(s.itemSpacingPx, this._view);
        const sampleLineLengthProj = PaperUtils.pixelToProjectDist(s.sampleLineLengthPx, this._view);
        const sampleLineWidthProj = PaperUtils.pixelToProjectDist(s.sampleLineWidthPx, this._view);
        const labelOffsetProj = PaperUtils.pixelToProjectDist(s.labelOffsetPx, this._view);
        const marginRightProj = PaperUtils.pixelToProjectDist(s.marginRightPx, this._view);
        const marginTopProj = PaperUtils.pixelToProjectDist(s.marginTopPx, this._view);
        const labelWidthProj = PaperUtils.pixelToProjectDist(s.labelWidthPx, this._view);
        const itemCount = this._visibleLegendItems.length;
        // Panel dimensions
        const panelHeight = paddingProj * 2 + itemSpacingProj * (itemCount - 1) + sampleLineWidthProj;
        const panelWidth = paddingProj * 2 + sampleLineLengthProj + labelOffsetProj + labelWidthProj;
        const panelRight = this._view.bounds.right - marginRightProj;
        const panelTop = this._view.bounds.bottom - marginTopProj;
        const panelLeft = panelRight - panelWidth;
        const panelBottom = panelTop - panelHeight;
        // Background rectangle
        const bgRect = new Path();
        bgRect.add(new Point(panelLeft, panelBottom));
        bgRect.add(new Point(panelRight, panelBottom));
        bgRect.add(new Point(panelRight, panelTop));
        bgRect.add(new Point(panelLeft, panelTop));
        bgRect.closed = true;
        bgRect.fillColor = new Color(s.backgroundColor);
        bgRect.strokeColor = null;
        this._legendGroup.addChild(bgRect);
        // Draw each item
        for (let i = 0; i < itemCount; i++) {
            const item = this._visibleLegendItems[i];
            // Y position: start near top of panel, descend each item (Y-inverted: subtract)
            const itemY = panelTop - paddingProj - i * itemSpacingProj;
            // Color sample line
            const sampleLine = new Path();
            sampleLine.strokeColor = new Color(item.color);
            sampleLine.strokeWidth = sampleLineWidthProj;
            sampleLine.add(new Point(panelLeft + paddingProj, itemY));
            sampleLine.add(new Point(panelLeft + paddingProj + sampleLineLengthProj, itemY));
            this._legendGroup.addChild(sampleLine);
            // Label text
            const labelX = panelLeft + paddingProj + sampleLineLengthProj + labelOffsetProj;
            const labelText = PaperUtils.createText(new Point(labelX, itemY), new Color(s.textColor), this._labels?.[item.dataKey] ??
                defaultLegendSettings.labels[item.dataKey], 1);
            labelText.justification = "left";
            PaperUtils.attenuateEntites(labelText, this._view, s.textSizePx);
            // Fix both X and Y position after attenuation scaling
            labelText.bounds.left = labelX;
            labelText.position.y = itemY;
            this._legendGroup.addChild(labelText);
        }
    }
    _renderMeasurementPanel(refIndex, entityPath) {
        const segmentPoints = entityPath.segments.map((s) => [
            s.point.x,
            s.point.y,
        ]);
        this._measurementPanelData = {
            segmentPoints,
            refIndex,
            cornerIndices: [],
        };
        this._measurementPanelGroup = new Group();
        // Create slope texts for each segment (same spans as measurement panel)
        const segments = entityPath.segments;
        const points = segments.map((s) => ({ x: s.point.x, y: s.point.y }));
        let cornerIndices = detectCorners(points);
        // Always include road center (refIndex) and road width endpoints (refIndex±1)
        if (!cornerIndices.includes(refIndex)) {
            cornerIndices.push(refIndex);
        }
        if (refIndex - 1 >= 0 && !cornerIndices.includes(refIndex - 1)) {
            cornerIndices.push(refIndex - 1);
        }
        if (refIndex + 1 < segmentPoints.length &&
            !cornerIndices.includes(refIndex + 1)) {
            cornerIndices.push(refIndex + 1);
        }
        // Remove duplicates and sort
        cornerIndices = [...new Set(cornerIndices)].sort((a, b) => a - b);
        // Filter out corners where the adjacent interval width < 0.001
        const filtered = [cornerIndices[0]];
        for (let i = 1; i < cornerIndices.length; i++) {
            const prevIdx = filtered[filtered.length - 1];
            const currIdx = cornerIndices[i];
            const width = Math.abs((segmentPoints[currIdx]?.[0] ?? 0) - (segmentPoints[prevIdx]?.[0] ?? 0));
            if (width >= 0.001) {
                filtered.push(currIdx);
            }
        }
        cornerIndices = filtered;
        // Save filtered corners for reuse in _drawMeasurementPanelContents
        this._measurementPanelData.cornerIndices = cornerIndices;
        if (cornerIndices.length < 2) {
            return;
        }
        // Split corners into left and right based on refIndex
        const leftCorners = [];
        const rightCorners = [];
        for (const idx of cornerIndices) {
            if (idx <= refIndex) {
                leftCorners.push(idx);
            }
            if (idx >= refIndex) {
                rightCorners.push(idx);
            }
        }
        // Sort corners
        leftCorners.sort((a, b) => b - a);
        rightCorners.sort((a, b) => a - b);
        // Helper to create slope text for a segment (auto-detection: percent or ratio)
        const createSlopeTexts = (startIdx, endIdx) => {
            const p1 = new Point(segmentPoints[startIdx]);
            const p2 = new Point(segmentPoints[endIdx]);
            const pos = p1.add(p2).multiply(0.5);
            const dy = p2.y - p1.y;
            const dx = p2.x - p1.x;
            const format = determineSlopeFormat(dy, dx);
            if (!format)
                return;
            if (format.type === "percent") {
                const percentSlopeText = PaperUtils.createText(pos, defaultMeasurementTextSettings.fillColor, format.text, 1);
                percentSlopeText.data.refPosition = pos.clone();
                percentSlopeText.data.moveDir = new Point(0, 1);
                this._percentSlopeTextRenderGroup.addChild(percentSlopeText);
            }
            else {
                const ratioSlopeText = PaperUtils.createText(pos, defaultMeasurementTextSettings.fillColor, format.text, 1);
                ratioSlopeText.data.refPosition = pos.clone();
                ratioSlopeText.data.moveDir = new Point(0, 1);
                this._ratioSlopeTextRenderGroup.addChild(ratioSlopeText);
            }
        };
        // Create slope texts for left side segments
        for (let i = 0; i < leftCorners.length - 1; i++) {
            createSlopeTexts(leftCorners[i], leftCorners[i + 1]);
        }
        // Create slope texts for right side segments
        for (let i = 0; i < rightCorners.length - 1; i++) {
            createSlopeTexts(rightCorners[i], rightCorners[i + 1]);
        }
        // Actual panel drawing deferred to _attenuate() which is called at end of renderData()
    }
    _drawMeasurementPanelContents() {
        if (!this._measurementPanelData || !this._measurementPanelGroup)
            return;
        const { segmentPoints, refIndex } = this._measurementPanelData;
        const heightProj = this._view.bounds.height * MEASUREMENT_PANEL_HEIGHT_RATIO;
        // view.bounds.top = 視覚的な画面下端（Y反転のため最小Y値）
        const panelBottom = this._view.bounds.top;
        const panelTop = panelBottom + heightProj;
        const panelLeft = this._view.bounds.left;
        const panelWidth = this._view.bounds.width;
        const arrowY = panelBottom + heightProj * 0.35;
        const arrowHeadSize = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.arrowHeadPixelSize, this._view);
        const lineWidth = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.verticalLinePixelSize, this._view);
        const dashProj = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.verticalLineDashPx[0], this._view);
        const gapProj = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.verticalLineDashPx[1], this._view);
        const panelTopLineWidth = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.panelTopLinePixelSize, this._view);
        const arrowLineWidth = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.doubleArrowPixelSize, this._view);
        // Background rectangle
        const bgRect = new Path();
        bgRect.add(new Point(panelLeft, panelBottom));
        bgRect.add(new Point(panelLeft + panelWidth, panelBottom));
        bgRect.add(new Point(panelLeft + panelWidth, panelTop));
        bgRect.add(new Point(panelLeft, panelTop));
        bgRect.closed = true;
        bgRect.fillColor = defaultMeasurementPanelSettings.backgroundColor;
        bgRect.strokeColor = null;
        this._measurementPanelGroup.addChild(bgRect);
        // Top border line of measurement panel
        const topBorder = new Path();
        topBorder.strokeColor = defaultMeasurementPanelSettings.panelTopLineColor;
        topBorder.strokeWidth = panelTopLineWidth;
        topBorder.add(new Point(panelLeft, panelTop));
        topBorder.add(new Point(panelLeft + panelWidth, panelTop));
        this._measurementPanelGroup.addChild(topBorder);
        // Use pre-computed filtered corner indices from _renderMeasurementPanel
        const cornerIndices = this._measurementPanelData.cornerIndices;
        if (cornerIndices.length < 2) {
            return;
        }
        // Split corners into left and right based on refIndex
        const leftCorners = [];
        const rightCorners = [];
        for (const idx of cornerIndices) {
            if (idx <= refIndex) {
                leftCorners.push(idx);
            }
            if (idx >= refIndex) {
                rightCorners.push(idx);
            }
        }
        // Sort left corners in reverse order (refIndex → 0)
        leftCorners.sort((a, b) => b - a);
        // Sort right corners in ascending order (refIndex → end)
        rightCorners.sort((a, b) => a - b);
        // Helper function to draw section (arrows, width text only - no vertical lines)
        const drawSection = (startIdx, endIdx) => {
            const p1 = segmentPoints[startIdx];
            const p2 = segmentPoints[endIdx];
            const x1 = p1[0];
            const x2 = p2[0];
            const midX = (x1 + x2) / 2;
            // Horizontal arrow line
            const arrowLine = new Path();
            arrowLine.strokeColor = defaultMeasurementPanelSettings.doubleArrowColor;
            arrowLine.strokeWidth = arrowLineWidth;
            arrowLine.add(new Point(x1, arrowY));
            arrowLine.add(new Point(x2, arrowY));
            this._measurementPanelGroup.addChild(arrowLine);
            // Use min/max so arrowheads always point inward (↔) regardless of x1/x2 order
            const xLeft = Math.min(x1, x2);
            const xRight = Math.max(x1, x2);
            // Left arrowhead (← tip at xLeft, body inward)
            const leftArrow = new Path();
            leftArrow.fillColor = defaultMeasurementPanelSettings.doubleArrowColor;
            leftArrow.strokeColor = null;
            leftArrow.add(new Point(xLeft, arrowY));
            leftArrow.add(new Point(xLeft + arrowHeadSize, arrowY + arrowHeadSize * 0.5));
            leftArrow.add(new Point(xLeft + arrowHeadSize, arrowY - arrowHeadSize * 0.5));
            leftArrow.closed = true;
            this._measurementPanelGroup.addChild(leftArrow);
            // Right arrowhead (→ tip at xRight, body inward)
            const rightArrow = new Path();
            rightArrow.fillColor = defaultMeasurementPanelSettings.doubleArrowColor;
            rightArrow.strokeColor = null;
            rightArrow.add(new Point(xRight, arrowY));
            rightArrow.add(new Point(xRight - arrowHeadSize, arrowY + arrowHeadSize * 0.5));
            rightArrow.add(new Point(xRight - arrowHeadSize, arrowY - arrowHeadSize * 0.5));
            rightArrow.closed = true;
            this._measurementPanelGroup.addChild(rightArrow);
            // Width text
            const widthTextOffsetPx = 10;
            const widthTextOffsetProj = PaperUtils.pixelToProjectDist(widthTextOffsetPx, this._view);
            const widthTextYRatio = arrowY + widthTextOffsetProj;
            const minMarginFromTopPx = 10;
            const minMarginProj = PaperUtils.pixelToProjectDist(minMarginFromTopPx, this._view);
            const widthTextYMax = panelTop - minMarginProj;
            const widthTextY = Math.min(widthTextYRatio, widthTextYMax);
            const dist = this._presentation.formatFixed(Math.abs(x2 - x1), 3) +
                this._presentation.getUnitLabel();
            const widthText = PaperUtils.createText(new Point(midX, widthTextY), defaultMeasurementPanelSettings.textFillColor, dist, 1);
            PaperUtils.attenuateEntites(widthText, this._view, defaultMeasurementTextSettings.defaultPixelSize);
            widthText.position = new Point(midX, widthTextY);
            this._measurementPanelGroup.addChild(widthText);
        };
        // Draw vertical dashed lines once per unique corner index
        for (const idx of cornerIndices) {
            const p = segmentPoints[idx];
            const vLine = new Path();
            vLine.strokeColor = defaultMeasurementPanelSettings.verticalLineColor;
            vLine.dashArray = [dashProj, gapProj];
            vLine.strokeWidth = lineWidth;
            vLine.add(new Point(p[0], p[1]));
            vLine.add(new Point(p[0], panelBottom));
            this._measurementPanelGroup.addChild(vLine);
        }
        // Process left side sections (reverse order: refIndex → 0)
        for (let i = 0; i < leftCorners.length - 1; i++) {
            const startIdx = leftCorners[i];
            const endIdx = leftCorners[i + 1];
            drawSection(startIdx, endIdx);
        }
        // Process right side sections (forward order: refIndex → end)
        for (let i = 0; i < rightCorners.length - 1; i++) {
            const startIdx = rightCorners[i];
            const endIdx = rightCorners[i + 1];
            drawSection(startIdx, endIdx);
        }
    }
    _createPath(pointArr, color) {
        const path = new Path();
        path.strokeColor = new Color(color);
        pointArr.forEach((arr) => {
            path.add(new Point(arr[0], arr[1]));
        });
        return path;
    }
    renderData(entityId, data, fit, presentation, entityType) {
        this._activateView();
        this.clearView();
        this._presentation = presentation;
        if (entityId === undefined || data === undefined)
            return;
        this._currentEntityId = entityId;
        const offsetData = {
            ...data,
            pathPoints: data.pathPoints.map((pathPoint) => {
                return [pathPoint[0], pathPoint[1]];
            }),
        };
        this._entityRenderGroup = new Group();
        this._measurementLinesGroup = new Group();
        this._ratioSlopeTextRenderGroup = new Group();
        this._percentSlopeTextRenderGroup = new Group();
        const rect = this._canvas.getBoundingClientRect();
        this.resizeView({ width: rect.width, height: rect.height });
        this._pathMap.set(entityId, offsetData);
        const pointCloudPaths = offsetData.pointCloudOutlines.map((p) => this._createPath(p, "#00FF00"));
        this._entityRenderGroup.addChildren(pointCloudPaths);
        const entityPath = this._createPath(offsetData.pathPoints, "#00FFFF");
        this._entityRenderGroup.addChild(entityPath);
        //fix to remove any undefined values
        offsetData.designDataOutlines = offsetData.designDataOutlines.filter((data) => data !== undefined);
        const designPaths = offsetData.designDataOutlines.map((p) => this._createPath(p, "#FF00FF"));
        this._entityRenderGroup.addChildren(designPaths);
        offsetData.referenceTopologyOutlines =
            offsetData.referenceTopologyOutlines.filter((data) => data !== undefined);
        const referenceTopologyPaths = offsetData.referenceTopologyOutlines.map((p) => this._createPath(p, "#A52A2A"));
        this._entityRenderGroup.addChildren(referenceTopologyPaths);
        const flatGroundPaths = offsetData.flatGroundDataOutlines?.map((p) => this._createPath(p, "#FF0000")) || [];
        this._entityRenderGroup.addChildren(flatGroundPaths);
        // All measurements will be calculated around the reference point index
        const refIndex = offsetData.referencePointIndex;
        if (refIndex >= 0) {
            // Render measurement panel (width display) and slope texts (same spans as panel)
            this._renderMeasurementPanel(refIndex, entityPath);
        }
        if (fit) {
            //fit the entities in current view
            const bounds = entityPath.bounds;
            const marginRatio = 0.03;
            const totalHeight = bounds.height / (1 - MEASUREMENT_PANEL_HEIGHT_RATIO - marginRatio);
            PaperUtils.fitView(this._view, bounds.center, bounds.width, totalHeight);
        }
        this.changeGridSize(this._gridSizeInPixel);
        // Build visible legend items and draw legend
        this._visibleLegendItems = filterVisibleLegendItems(offsetData);
        if (this._visibleLegendItems.length > 0) {
            this._legendGroup = new Group();
        }
        this._attenuate();
    }
    setBackgroundColor(color) {
        const tempColor = color ? color : "#566573";
        this._canvas.style.backgroundColor = tempColor;
    }
    updatePresentation(presentation) {
        this._presentation = presentation;
        if (this._currentEntityId) {
            const data = this._pathMap.get(this._currentEntityId);
            if (data) {
                this.renderData(this._currentEntityId, data, false, presentation);
                return;
            }
        }
        this._activateView();
        this.changeGridSize(this._gridSizeInPixel);
    }
    changeGridSize(step) {
        this._activateView();
        PaperUtils.createGrid(this._view, step, this._gridGroup, this._gridText, this._presentation);
        this._attenuate();
    }
    getCanvas() {
        return this._canvas;
    }
    deleteEntityPath(id) {
        if (this._pathMap.delete(id) && this._currentEntityId === id) {
            this._currentEntityId = undefined;
            this.clearView();
        }
    }
}
//# sourceMappingURL=view-2d-section.js.map